import { Clock, Coffee, UtensilsCrossed, Wine } from "lucide-react";
import { Link } from "react-router-dom";

import { Layout } from "@/components/layout";

export function Dining() {
  const menuHighlights = [
    {
      name: "Fresh Seafood Platter",
      description: "Daily catch grilled to perfection with Thai herbs and local spices",
      category: "Signature Dishes",
    },
    {
      name: "Thai Masaman Curry",
      description: "Traditional slow-cooked curry with tender beef and roasted peanuts",
      category: "Signature Dishes",
    },
    {
      name: "Lobster Thermidor",
      description: "Succulent lobster in creamy cognac sauce with gruyere crust",
      category: "Signature Dishes",
    },
    {
      name: "Mango Sticky Rice",
      description: "Classic Thai dessert with fresh mango and coconut cream",
      category: "Desserts",
    },
  ];

  const cocktails = [
    {
      name: "Amor Sunset",
      description: "Passion fruit, rum, and champagne with a touch of Thai basil",
    },
    {
      name: "Tropical Paradise",
      description: "Coconut vodka, pineapple, and lime with butterfly pea flower",
    },
    {
      name: "Thai Mojito",
      description: "Fresh mint, lemongrass, lime, and premium white rum",
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/19533265/pexels-photo-19533265.jpeg?auto=compress&cs=tinysrgb&w=1920)",
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              Dining Experience
            </h1>
            <p className="text-xl text-white/95 font-light tracking-wide">
              A culinary journey where Thai tradition meets contemporary elegance
            </p>
          </div>
        </div>
      </section>

      {/* Restaurant Introduction */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-6">
            The Amor Restaurant
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Our beachfront restaurant offers an exquisite dining experience where the freshest local
            ingredients meet refined culinary artistry. With panoramic ocean views and the sound of
            waves as your backdrop, every meal becomes a memorable occasion.
          </p>
          <p className="text-lg text-stone-700 leading-relaxed">
            Our chefs blend authentic Thai flavors with international techniques, creating dishes
            that honor tradition while embracing innovation. From sunrise breakfast to romantic
            candlelit dinners under the stars.
          </p>
        </div>
      </section>

      {/* Restaurant Image & Info */}
      <section className="py-16 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[400px]">
              <img
                src="https://images.pexels.com/photos/2566037/pexels-photo-2566037.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Fine Dining"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <h3 className="font-heading text-3xl font-bold text-stone-900 mb-6">
                Cuisine & Philosophy
              </h3>
              <p className="text-stone-700 leading-relaxed mb-6">
                We celebrate the bounty of Samui's seas and the richness of Thai culinary heritage.
                Each morning, our chefs personally select the finest seafood from local fishermen,
                ensuring every dish showcases the island's natural flavors at their peak.
              </p>
              <p className="text-stone-700 leading-relaxed mb-8">
                Our menu changes with the seasons, highlighting the best produce Thailand has to
                offer while incorporating international favorites for our global guests.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-900 flex items-center justify-center flex-shrink-0">
                    <UtensilsCrossed size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-1">Farm to Table</h4>
                    <p className="text-sm text-stone-600">
                      Organic produce from local farms and our own garden
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-900 flex items-center justify-center flex-shrink-0">
                    <Wine size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-1">Curated Wine Selection</h4>
                    <p className="text-sm text-stone-600">
                      Carefully selected wines from around the world
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Highlights */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-4">
            Signature Selections
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-12" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {menuHighlights.map((item, index) => (
              <div key={index} className="border-b border-stone-200 pb-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-heading text-xl font-bold text-stone-900">{item.name}</h3>
                  <span className="text-xs tracking-wider text-stone-500 uppercase">
                    {item.category}
                  </span>
                </div>
                <p className="text-stone-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bar & Cocktails */}
      <section className="py-16 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">The Amor Bar</h2>
              <div className="w-24 h-px bg-luxury-gold mb-8" />
              <p className="text-stone-300 leading-relaxed mb-8">
                As the sun sets over the Gulf of Thailand, our beachfront bar comes alive. Expert
                mixologists craft innovative cocktails using fresh tropical fruits, premium spirits,
                and Thai-inspired ingredients. Whether you prefer a classic cocktail or an
                adventurous new creation, our bar offers the perfect setting for sunset drinks and
                evening relaxation.
              </p>

              <h3 className="font-heading text-2xl font-bold mb-6">Featured Cocktails</h3>
              <div className="space-y-4">
                {cocktails.map((cocktail, index) => (
                  <div key={index} className="border-b border-stone-800 pb-4">
                    <h4 className="font-semibold text-white mb-1">{cocktail.name}</h4>
                    <p className="text-sm text-stone-400">{cocktail.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative h-[500px]">
              <img
                src="https://images.pexels.com/photos/8922201/pexels-photo-8922201.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="The Amor Bar"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dining Hours */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-12">
            Hours of Service
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 mb-4">
                <Coffee size={28} className="text-stone-900" />
              </div>
              <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Breakfast</h3>
              <div className="flex items-center justify-center gap-2 text-stone-600 mb-2">
                <Clock size={16} />
                <span>7:00 AM - 11:00 AM</span>
              </div>
              <p className="text-sm text-stone-600">Continental and à la carte options</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 mb-4">
                <UtensilsCrossed size={28} className="text-stone-900" />
              </div>
              <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Lunch</h3>
              <div className="flex items-center justify-center gap-2 text-stone-600 mb-2">
                <Clock size={16} />
                <span>12:00 PM - 3:00 PM</span>
              </div>
              <p className="text-sm text-stone-600">Light meals and fresh seafood</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 mb-4">
                <Wine size={28} className="text-stone-900" />
              </div>
              <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Dinner</h3>
              <div className="flex items-center justify-center gap-2 text-stone-600 mb-2">
                <Clock size={16} />
                <span>6:00 PM - 10:30 PM</span>
              </div>
              <p className="text-sm text-stone-600">Fine dining under the stars</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-stone-700 mb-6">Reservations recommended for dinner service</p>
            <Link
              to="/book"
              className="bg-stone-800 text-white px-8 py-3 text-sm tracking-widest uppercase font-semibold hover:bg-stone-700 transition-colors inline-block"
            >
              Make a Reservation
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
